import { Popover, PopoverTrigger, Tooltip, PopoverContent } from '@ttab/elephant-ui'
import { ClockIcon } from '@/components/ClockIcon'
import { format, parseISO } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { dateInTimestampOrShortMonthDayTimestamp, newLocalDate, parseDate } from '@/shared/datetime'
import { DEFAULT_TIMEZONE } from '@/defaults/defaultTimezone'
import { useQuery } from '@/hooks/useQuery'
import type { AssignmentInterface } from '@/hooks/index/useAssignments'
import type { StatusData } from '@/types'
import { useRegistry } from '@/hooks/useRegistry'
import { useMemo } from 'react'
import { timesSlots } from '@/defaults/assignmentTimeslots'
import type { LocaleData } from '@/types/index'
import { useTranslation } from 'react-i18next'
import { showTranslatedText } from '@/lib/showTranslatedText'
import type { TFunction } from 'i18next'

export const TimeCard = ({ assignment }: { assignment: AssignmentInterface }) => {
  const [query] = useQuery()
  const { timeZone, locale } = useRegistry()
  const { t } = useTranslation()

  const compareDate = useMemo(() => (
    typeof query?.from === 'string'
      ? parseDate(query.from)
      : newLocalDate(DEFAULT_TIMEZONE)
  ), [query?.from])

  const statusData: StatusData | null = useMemo(() => (
    assignment?._statusData
      ? JSON.parse(assignment._statusData) as StatusData
      : null
  ), [assignment?._statusData])

  const time = useMemo(() =>
    getAssignmentTime({ assignment, timeZone, locale, statusData, compareDate, t }),
  [assignment, timeZone, locale, statusData, compareDate, t]
  )

  const timeTooltip = useMemo(() =>
    getTimeTooltip({ assignment, statusData, timeZone, locale, compareDate, t }),
  [assignment, statusData, timeZone, locale, compareDate, t]
  )

  return (
    <Popover>
      <PopoverTrigger onClick={(e) => e.stopPropagation()}>
        <Tooltip content={timeTooltip}>
          <div className='flex flex-row gap-1 items-center'>
            <ClockIcon hour={time?.hour} size={14} className='opacity-50' />
            <time>{time?.label}</time>
          </div>
        </Tooltip>
      </PopoverTrigger>
      <PopoverContent className='lg:hidden'>
        {timeTooltip}
      </PopoverContent>
    </Popover>
  )
}

export function getAssignmentTime({ assignment, timeZone, locale, statusData, compareDate, t }: {
  assignment: AssignmentInterface
  timeZone: string
  locale: LocaleData
  statusData: StatusData | null
  compareDate?: Date
  t: TFunction
}): { label?: string, hour?: number } | undefined {
  if (
    assignment._deliverableStatus === 'draft'
    && !statusData?.workflowCheckpoint
    && assignment.data.publish_slot
  ) {
    return { label: getTimeslotLabel(parseInt(assignment.data.publish_slot), t) }
  }

  if (assignment.data.publish && assignment._deliverableStatus === 'withheld') {
    return clockTime(assignment.data.publish, timeZone)
  }

  const statusTime = getUsableAt(assignment, statusData) ?? statusData?.modified
  if (statusTime) {
    return {
      label: formatStatusTime(statusTime, timeZone, locale, compareDate),
      hour: zonedHour(statusTime, timeZone)
    }
  }

  if (assignment.data.start) {
    return clockTime(assignment.data.start, timeZone)
  }

  return undefined
}

export function getTimeslotLabel(hour: number, t: TFunction): string | undefined {
  for (const key in timesSlots) {
    if (timesSlots[key].slots.includes(hour)) {
      return showTranslatedText(timesSlots[key].label, t)
    }
  }
  return undefined
}

function getTimeTooltip({ assignment, statusData, timeZone, locale, compareDate, t }: {
  assignment: AssignmentInterface
  statusData: StatusData | null
  timeZone: string
  locale: LocaleData
  compareDate?: Date
  t: (key: string) => string
}): string {
  if (assignment.data.publish && assignment._deliverableStatus === 'withheld') {
    const time = format(toZonedTime(parseISO(assignment.data.publish), timeZone), 'HH:mm')
    return `${t('core:status.withheld')} ${time}`
  }
  const usableAt = getUsableAt(assignment, statusData)
  if (usableAt) {
    return `${t('core:status.usable')} ${formatStatusTime(usableAt, timeZone, locale, compareDate)}`
  }
  if (statusData?.modified) {
    const time = formatStatusTime(statusData.modified, timeZone, locale, compareDate)
    return `${t('views:approvals.tooltips.lastChanged')} ${time}`
  }
  return t('views:approvals.tooltips.lastChanged')
}

function formatStatusTime(
  time: string,
  timeZone: string,
  locale: LocaleData,
  compareDate?: Date
): string {
  return compareDate
    ? dateInTimestampOrShortMonthDayTimestamp(time, locale.code.full, timeZone, compareDate)
    : format(toZonedTime(parseISO(time), timeZone), 'HH:mm')
}

function getUsableAt(
  assignment: AssignmentInterface,
  statusData: StatusData | null
): string | undefined {
  return assignment._deliverableStatus === 'usable' ? statusData?.heads.usable?.created : undefined
}

function clockTime(time: string, timeZone: string): { label: string, hour: number } {
  const zoned = toZonedTime(parseISO(time), timeZone)
  return { label: format(zoned, 'HH:mm'), hour: zoned.getHours() }
}

function zonedHour(time: string, timeZone: string): number {
  return toZonedTime(parseISO(time), timeZone).getHours()
}
